/**
 * @fileoverview Notes Editor — pared-back TipTap rich text editor for
 * contact-style notes fields.
 *
 * Sibling to the precedent `TemplateEditor` but trimmed to a small
 * toolbar (Bold, Italic, Underline, lists, link). Outputs HTML; legacy
 * plain-text notes are valid HTML so no migration is required when
 * swapping the existing `<Textarea>` for this component.
 *
 * Design notes:
 * - `value` is the source of truth (controlled). The component avoids
 *   reusing the same editor instance across different contact rows by
 *   resetting the document whenever `value` changes from the outside —
 *   the parent should pass a stable `key` to force a fresh editor when
 *   it switches to a different record.
 * - `onAutosave` fires on blur and on calling `flush()` via the imperative
 *   ref. Use it with the existing `useAutosave` hook on the parent.
 * - Trailing toolbar items (AI menu, voice mic) plug in via
 *   `toolbarTrailing` so the same shell can be reused across the
 *   notes-ai-assist and voice-toolbar increments without rewiring this
 *   file.
 */

import { useEditor, useEditorState, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import type { Editor } from "@tiptap/react";
import type { EditorView } from "@tiptap/pm/view";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Loader2,
  Mic,
  Sparkles,
  Square,
  Underline as UnderlineIcon,
  Wand2,
  X,
} from "lucide-react";
import { useAction } from "convex/react";
import { api } from "@cvx/_generated/api";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { isDictationControlShown } from "@/ui/hooks/use-chat-voice-input";
import {
  useRealtimeTranscription,
  type TranscriptionState,
} from "@/ui/hooks/use-realtime-transcription";
import { cn } from "@/utils/misc";
import { aiBubbleMenuOptions, aiErrorMessage } from "./notes-editor.helpers";
import { NotesDraftPopover } from "./notes-draft-popover";

// ---------------------------------------------------------------------------
// AI polish modes
// ---------------------------------------------------------------------------

type PolishMode = "rewrite" | "summarise" | "fix_grammar" | "make_formal";

const AI_MODES: ReadonlyArray<{
  id: PolishMode;
  label: string;
  description: string;
}> = [
  {
    id: "rewrite",
    label: "Rewrite",
    description: "Reword the selection while keeping every fact intact.",
  },
  {
    id: "summarise",
    label: "Summarise",
    description: "Compress the selection to one or two short sentences.",
  },
  {
    id: "fix_grammar",
    label: "Fix grammar",
    description: "Spelling / punctuation only, no rephrasing.",
  },
  {
    id: "make_formal",
    label: "Make formal",
    description: "Lift the tone to formal case-file English.",
  },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NotesEditorHandle {
  /** Force-flush whatever is in the editor right now (e.g. on dialog close). */
  flush: () => void;
  /** Insert plain text at the current selection. Used by voice transcription. */
  insertText: (text: string) => void;
  /** Get the live editor instance (advanced use, e.g. BubbleMenu). */
  getEditor: () => Editor | null;
}

interface NotesEditorProps {
  /** HTML (or plain text — also valid HTML) for the editor's initial document. */
  value: string;
  /**
   * Fires on every editor change. Use this to keep parent draft state
   * in sync so the parent's existing `useAutosave` hook debounces and
   * persists the value.
   */
  onChange?: (html: string) => void;
  /** Optional explicit save callback (fires on blur and on `flush()`). */
  onAutosave?: (html: string) => void;
  /** Defaults to true. Set false for read-only preview mode. */
  editable?: boolean;
  placeholder?: string;
  /** Tailwind class for the editor's min-height (e.g. `min-h-[200px]`). */
  minHeightClass?: string;
  /** Tailwind class applied to the wrapper. */
  className?: string;
  /**
   * Optional trailing toolbar slot — e.g. AI bubble trigger, mic button.
   * Receives the live editor so callers can inspect/dispatch commands.
   */
  toolbarTrailing?: (editor: Editor) => React.ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const NotesEditor = forwardRef<NotesEditorHandle, NotesEditorProps>(
  function NotesEditor(
    {
      value,
      onChange,
      onAutosave,
      editable = true,
      placeholder,
      minHeightClass = "min-h-[160px]",
      className,
      toolbarTrailing,
    },
    ref,
  ) {
    const lastEmittedRef = useRef<string>(value);

    const extensions = useMemo(
      () => [
        StarterKit.configure({
          // We don't need code blocks or blockquote for short notes; keep the
          // toolbar light. Headings are limited to h3 so notes don't compete
          // visually with the surrounding panel.
          heading: { levels: [3] },
          codeBlock: false,
          blockquote: false,
          horizontalRule: false,
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: {
            class: "text-primary underline underline-offset-2",
            rel: "noopener noreferrer",
            target: "_blank",
          },
        }),
      ],
      [],
    );

    const editor = useEditor({
      extensions,
      content: value || "",
      editable,
      onUpdate: ({ editor: e }) => {
        if (!editable) return;
        const html = serialise(e.getHTML());
        if (html === lastEmittedRef.current) return;
        lastEmittedRef.current = html;
        onChange?.(html);
      },
      onBlur: ({ editor: e }) => {
        if (!editable) return;
        const html = serialise(e.getHTML());
        onAutosave?.(html);
      },
      editorProps: {
        attributes: {
          class: cn(
            "prose prose-sm max-w-none px-3 py-2 dark:prose-invert focus:outline-none",
            minHeightClass,
          ),
          "data-placeholder": placeholder ?? "",
        },
      },
    });

    // Keep editor `editable` in sync with prop changes.
    useEffect(() => {
      if (!editor) return;
      editor.setEditable(editable);
    }, [editor, editable]);

    // If the parent flips `value` from outside (e.g. switching to a
    // different contact row), replace the document. Skip when the value
    // matches what we just emitted to avoid clobbering live edits.
    useEffect(() => {
      if (!editor) return;
      const incoming = value || "";
      if (incoming !== lastEmittedRef.current) {
        lastEmittedRef.current = incoming;
        editor.commands.setContent(incoming, { emitUpdate: false });
      }
    }, [editor, value]);

    useImperativeHandle(
      ref,
      () => ({
        flush: () => {
          if (!editor || !editable) return;
          const html = serialise(editor.getHTML());
          onAutosave?.(html);
        },
        insertText: (text: string) => {
          if (!editor || !editable) return;
          editor.chain().focus().insertContent(text).run();
        },
        getEditor: () => editor,
      }),
      [editor, editable, onAutosave],
    );

    // Voice transcription state lives on the parent so the toolbar
    // button (a small icon) and the body-level transcript banner (a
    // full-width readable area) can share a single source of truth
    // without prop drilling through context.
    const voice = useVoiceTranscription(editor);

    // The editable area (below the toolbar) bounds the AI bubble menu so it
    // never covers the formatting controls — see `aiBubbleMenuOptions`. The
    // menu mounts only once this element exists: TipTap's React BubbleMenu
    // ignores the first options change after its plugin initialises, so a
    // boundary arriving one render late would never be applied.
    const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
    // The whole editor shell (toolbar + content + menu): focus leaving it
    // dismisses the AI menu, focus moving within it (e.g. chip → Bold) does not.
    const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);

    if (!editor) return null;

    return (
      <div
        ref={setRootEl}
        className={cn(
          "rounded-md border border-input bg-background",
          !editable && "border-dashed bg-muted/20",
          className,
        )}
      >
        {editable && (
          <Toolbar
            editor={editor}
            voice={voice}
            toolbarTrailing={toolbarTrailing}
          />
        )}
        {editable && (
          <VoiceTranscriptBanner
            state={voice.state}
            interim={voice.interim}
            elapsedMs={voice.elapsedMs}
          />
        )}
        <EditorContent editor={editor} ref={setContentEl} />
        {editable && contentEl && rootEl && (
          <AiBubbleMenu
            editor={editor}
            boundary={contentEl}
            focusScope={rootEl}
          />
        )}
      </div>
    );
  },
);

// ---------------------------------------------------------------------------
// Voice transcription — shared hook
// ---------------------------------------------------------------------------

/**
 * Internal shape exposed by `useVoiceTranscription`. Combines hook
 * state (listening / processing / interim text / elapsed timer) with a
 * single `onToggle` action so the toolbar button and the body banner
 * can both render against the same record.
 */
interface VoiceState {
  state: TranscriptionState;
  interim: string;
  elapsedMs: number;
  isListening: boolean;
  isProcessing: boolean;
  onToggle: () => void;
  /**
   * False when this deployment has dictation off: no toolbar mic (an open
   * session keeps its stop control; `isDictationControlShown`).
   */
  isEnabled: boolean;
}

/**
 * Owns the OpenAI Realtime transcription session for a single notes
 * editor instance. Streams interim deltas into local state (rendered
 * in `VoiceTranscriptBanner`) and inserts the final transcript into
 * the editor as a single insertion when the session ends.
 *
 * Hoisted to the editor parent so the toolbar mic button and the
 * full-width transcript banner share state without React context.
 */
function useVoiceTranscription(editor: Editor | null): VoiceState {
  const editorRef = useRef<Editor | null>(editor);
  editorRef.current = editor;

  const interimRef = useRef<string>("");
  const [interim, setInterim] = useState<string>("");
  const [elapsedMs, setElapsedMs] = useState<number>(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);

  const insertFinal = useCallback((text: string) => {
    const e = editorRef.current;
    if (!e) return;
    const trimmed = text.trim();
    if (trimmed.length === 0) return;

    // Decide whether to add a leading space so the new text doesn't
    // run on into the previous word (or follow a newline awkwardly).
    const { from } = e.state.selection;
    const before = e.state.doc.textBetween(Math.max(0, from - 1), from, " ");
    const needsLeadingSpace = before.length > 0 && !/\s/.test(before);
    const insertion = needsLeadingSpace ? ` ${trimmed}` : trimmed;

    e.chain().focus().insertContent(insertion).run();
  }, []);

  const transcription = useRealtimeTranscription({
    onDelta: (delta) => {
      interimRef.current = `${interimRef.current}${delta}`;
      setInterim(interimRef.current);
    },
    onFinal: (final) => {
      interimRef.current = "";
      setInterim("");
      insertFinal(final);
    },
    onError: (message) => {
      toast.error(message);
    },
  });

  const isListening = transcription.state === "listening";
  const isProcessing = transcription.state === "processing";

  // Drive the elapsed-time pill only while the mic is actually open.
  useEffect(() => {
    if (isListening) {
      startedAtRef.current = Date.now();
      setElapsedMs(0);
      tickRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startedAtRef.current);
      }, 250);
    } else {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      setElapsedMs(0);
    }
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [isListening]);

  // When the session fully ends, clear the interim text. Done in a
  // separate effect so the interim survives the listening → processing
  // transition and stays visible alongside the "Finalising…" pill.
  useEffect(() => {
    if (transcription.state === "idle") {
      interimRef.current = "";
      setInterim("");
    }
  }, [transcription.state]);

  const onToggle = useCallback(() => {
    if (isListening) {
      transcription.stop();
    } else if (!isProcessing) {
      void transcription.start();
    }
  }, [isListening, isProcessing, transcription]);

  return {
    state: transcription.state,
    interim,
    elapsedMs,
    isListening,
    isProcessing,
    onToggle,
    isEnabled: transcription.isEnabled,
  };
}

// ---------------------------------------------------------------------------
// Live transcript banner
// ---------------------------------------------------------------------------

/**
 * Full-width banner that appears below the toolbar while voice
 * dictation is active. Shows the live interim transcript (streamed
 * deltas from the Realtime API) in readable type so the practitioner
 * can see exactly what the model is hearing. Cleared when the
 * session moves to "processing" and the cleaned final transcript
 * flushes into the editor body.
 */
interface VoiceTranscriptBannerProps {
  state: TranscriptionState;
  interim: string;
  elapsedMs: number;
}

function VoiceTranscriptBanner({
  state,
  interim,
  elapsedMs,
}: VoiceTranscriptBannerProps) {
  if (state === "idle") return null;
  return (
    <div
      className={cn(
        "border-b border-border px-3 py-2 text-sm",
        state === "listening"
          ? "bg-destructive/5"
          : "bg-amber-50 dark:bg-amber-950/30",
      )}
    >
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider">
        {state === "listening" ? (
          <>
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-destructive" />
            <span className="text-destructive">Listening</span>
            <span className="font-mono text-destructive/70">
              {formatElapsed(elapsedMs)}
            </span>
          </>
        ) : (
          <>
            <Loader2 className="h-3 w-3 animate-spin text-amber-700 dark:text-amber-400" />
            <span className="text-amber-700 dark:text-amber-400">
              Finalising transcript…
            </span>
          </>
        )}
      </div>
      <p className="leading-relaxed text-foreground/85">
        {interim || (
          <span className="italic text-muted-foreground">
            {state === "listening"
              ? "Start speaking — the transcript will appear here in real time."
              : "Cleaning up the transcript…"}
          </span>
        )}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

function Toolbar({
  editor,
  voice,
  toolbarTrailing,
}: {
  editor: Editor;
  voice: VoiceState;
  toolbarTrailing?: (editor: Editor) => React.ReactNode;
}) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      isBold: e?.isActive("bold") ?? false,
      isItalic: e?.isActive("italic") ?? false,
      isUnderline: e?.isActive("underline") ?? false,
      isBulletList: e?.isActive("bulletList") ?? false,
      isOrderedList: e?.isActive("orderedList") ?? false,
      isLink: e?.isActive("link") ?? false,
    }),
  });

  return (
    <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1">
      <ToolbarButton
        active={state?.isBold ?? false}
        onClick={() => editor.chain().focus().toggleBold().run()}
        ariaLabel="Bold"
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={state?.isItalic ?? false}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        ariaLabel="Italic"
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={state?.isUnderline ?? false}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        ariaLabel="Underline"
      >
        <UnderlineIcon className="h-3.5 w-3.5" />
      </ToolbarButton>

      <Separator />

      <ToolbarButton
        active={state?.isBulletList ?? false}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        ariaLabel="Bullet list"
      >
        <List className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={state?.isOrderedList ?? false}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        ariaLabel="Numbered list"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolbarButton>

      <Separator />

      <LinkPopover editor={editor} isLink={state?.isLink ?? false} />

      <div className="ml-auto flex items-center gap-0.5">
        <NotesDraftPopover editor={editor} />
        {isDictationControlShown(voice) && <VoiceToggle voice={voice} />}
        {toolbarTrailing?.(editor)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Voice toggle (push-to-talk mic / stop)
// ---------------------------------------------------------------------------

/**
 * Toolbar button that flips the voice transcription session on and off.
 *
 * Three visual states, driven by the shared `VoiceState`:
 * - **Idle** — neutral mic icon. Click to start recording.
 * - **Listening** — solid red square (universal "stop recording"
 *   affordance). Deliberately NOT a `MicOff` icon, which reads as
 *   "microphone is muted" rather than "click here to stop".
 * - **Processing** — spinner, button disabled while we wait for the
 *   final transcript to come back from the API.
 *
 * The live transcript itself renders in `VoiceTranscriptBanner`
 * directly below the toolbar so the practitioner can see the streamed
 * deltas as they arrive — no truncation, no need to hover a tooltip.
 */
function VoiceToggle({ voice }: { voice: VoiceState }) {
  const { isListening, isProcessing, onToggle } = voice;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={
        isListening
          ? "Stop dictation"
          : isProcessing
            ? "Finalising transcript"
            : "Start dictation"
      }
      aria-pressed={isListening}
      disabled={isProcessing}
      title={
        isListening
          ? "Stop dictation"
          : isProcessing
            ? "Finalising transcript…"
            : "Dictate via voice (auto-stops after 5 minutes)"
      }
      className={cn(
        "h-7 w-7",
        isListening &&
          "bg-destructive/10 text-destructive hover:bg-destructive/20",
        isProcessing &&
          "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
      )}
      onClick={onToggle}
    >
      {isListening ? (
        <Square className="h-3 w-3 fill-current" />
      ) : isProcessing ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Mic className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function ToolbarButton({
  active,
  onClick,
  ariaLabel,
  children,
}: {
  active: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={ariaLabel}
      aria-pressed={active}
      className={cn("h-7 w-7", active && "bg-muted text-foreground")}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function Separator() {
  return <div className="mx-1 h-4 w-px bg-border" />;
}

function LinkPopover({ editor, isLink }: { editor: Editor; isLink: boolean }) {
  const [open, setOpen] = useState(false);
  const [href, setHref] = useState("");

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        const current = editor.getAttributes("link").href as string | undefined;
        setHref(current ?? "");
      }
      setOpen(next);
    },
    [editor],
  );

  const apply = () => {
    const trimmed = href.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: trimmed })
        .run();
    }
    setOpen(false);
  };

  const remove = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Link"
          aria-pressed={isLink}
          className={cn("h-7 w-7", isLink && "bg-muted text-foreground")}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-2">
          <Input
            placeholder="https://…"
            value={href}
            onChange={(e) => setHref(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                apply();
              }
            }}
            autoFocus
          />
          <div className="flex items-center justify-between gap-2">
            {isLink ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={remove}
                className="gap-1"
              >
                <X className="h-3 w-3" />
                Remove
              </Button>
            ) : (
              <span />
            )}
            <Button size="sm" onClick={apply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// AI bubble menu
// ---------------------------------------------------------------------------

/**
 * Plugin key for the AI bubble menu, so the component can ask TipTap to
 * re-run positioning when the menu changes size (chip → action row).
 */
const AI_BUBBLE_MENU_KEY = "notesAiBubbleMenu";

/**
 * Floating menu shown on text selection. It opens as a compact sparkle
 * "AI" chip; clicking the chip expands the four polish actions, each of
 * which runs a single-shot Claude rewrite and replaces the selected range
 * with the result. The expanded state is keyed to the selection range, so
 * any change of selection (or dismissal) collapses it again.
 * Positioned inside `boundary` (the editable area) so it flips below the
 * selection rather than covering the formatting toolbar. Hidden and
 * collapsed whenever focus leaves `focusScope` (the editor shell), since
 * TipTap only hides on the editor's own blur and, once the chip has taken
 * focus, that blur never fires when the user moves to another editor.
 *
 * Intentionally lightweight: no thread, no proposeRewrite tool, no
 * apply/reject card. The selection itself is the diff.
 */
function AiBubbleMenu({
  editor,
  boundary,
  focusScope,
}: {
  editor: Editor;
  boundary: HTMLElement;
  focusScope: HTMLElement;
}) {
  const polishNote = useAction(api.contacts.notesAi.polishNote);
  const [busyMode, setBusyMode] = useState<PolishMode | null>(null);
  const options = useMemo(() => aiBubbleMenuOptions(boundary), [boundary]);
  const busyRef = useRef<PolishMode | null>(null);
  busyRef.current = busyMode;

  const selectionKey = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e ? `${e.state.selection.from}:${e.state.selection.to}` : "",
  });
  const [expandedFor, setExpandedFor] = useState<string | null>(null);
  const expanded = expandedFor !== null && expandedFor === selectionKey;

  // Any change of selection collapses the row for good — re-selecting the
  // same range later starts from the chip again.
  useEffect(() => {
    setExpandedFor((current) =>
      current !== null && current !== selectionKey ? null : current,
    );
  }, [selectionKey]);

  const menuRef = useRef<HTMLDivElement | null>(null);

  // The chip and the action row differ in size, so re-run Floating UI's
  // placement once the expanded row has rendered. The chip that had focus
  // has just unmounted, so re-home focus on the first action: keyboard
  // users land on Rewrite and focus stays inside the editor shell.
  useEffect(() => {
    if (!expanded) return;
    editor.view.dispatch(
      editor.state.tr.setMeta(AI_BUBBLE_MENU_KEY, "updatePosition"),
    );
    menuRef.current?.querySelector("button")?.focus();
  }, [editor, expanded]);

  useEffect(() => {
    const pending = { timer: 0 };
    // Decide once focus has settled, not at the focusout itself: the chip
    // React unmounts on click fires focusout (no relatedTarget, still
    // connected) before the row exists, a dialog focus trap may then grab
    // focus, and only after that does the expand effect land focus on the
    // first action. One tick later `activeElement` tells the truth.
    const onFocusOut = () => {
      window.clearTimeout(pending.timer);
      pending.timer = window.setTimeout(() => {
        if (editor.isDestroyed) return;
        const active = document.activeElement;
        if (active instanceof Node && focusScope.contains(active)) return;
        setExpandedFor(null);
        editor.view.dispatch(
          editor.state.tr.setMeta(AI_BUBBLE_MENU_KEY, "hide"),
        );
      }, 0);
    };
    focusScope.addEventListener("focusout", onFocusOut);
    return () => {
      window.clearTimeout(pending.timer);
      focusScope.removeEventListener("focusout", onFocusOut);
    };
  }, [editor, focusScope]);

  const shouldShow = useCallback(
    ({
      editor: e,
      view,
      element,
      from,
      to,
    }: {
      editor: Editor;
      view: EditorView;
      element: HTMLElement;
      from: number;
      to: number;
    }) => {
      if (!e.isEditable) return false;
      // Same rule as TipTap's default menu: the editor or the menu itself
      // must have focus, so a selection left behind in an unfocused editor
      // shows nothing.
      if (!view.hasFocus() && !element.contains(document.activeElement)) {
        return false;
      }
      if (from === to) return false;
      const text = e.state.doc.textBetween(from, to, " ").trim();
      return text.length >= 2;
    },
    [],
  );

  const runMode = async (mode: PolishMode) => {
    if (busyRef.current) return;
    const { from, to } = editor.state.selection;
    if (from === to) return;

    const selectedText = editor.state.doc.textBetween(from, to, "\n").trim();
    if (selectedText.length === 0) return;

    setBusyMode(mode);
    try {
      const result = await polishNote({ text: selectedText, mode });
      const cleaned = result.text.trim();
      if (cleaned.length === 0) {
        toast.error("AI returned an empty response. Try again.");
        return;
      }
      editor
        .chain()
        .focus()
        .deleteRange({ from, to })
        .insertContent(cleaned)
        .run();
    } catch (err) {
      toast.error(aiErrorMessage(err, "AI polish failed. Try again."));
    } finally {
      setBusyMode(null);
    }
  };

  return (
    <BubbleMenu
      ref={menuRef}
      editor={editor}
      pluginKey={AI_BUBBLE_MENU_KEY}
      shouldShow={shouldShow}
      options={options}
      // `flex-wrap` pairs with the `size` middleware in `aiBubbleMenuOptions`,
      // which caps max-width to the editor so a narrow editor wraps the
      // actions instead of overflowing its edges.
      className="flex flex-wrap items-center gap-0.5 rounded-md border border-border bg-popover p-1 shadow-md"
    >
      {!expanded ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="AI actions"
          aria-expanded={false}
          title="Rewrite, summarise, fix grammar or make formal"
          onClick={() => setExpandedFor(selectionKey)}
          className="h-7 gap-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
        >
          <Sparkles className="h-3 w-3 text-primary" />
          AI
        </Button>
      ) : (
        <span
          aria-expanded={true}
          className="flex items-center gap-1 px-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
        >
          <Sparkles className="h-3 w-3 text-primary" />
          AI
        </span>
      )}
      {expanded && <div className="mx-0.5 h-4 w-px bg-border" />}
      {expanded &&
        AI_MODES.map((mode) => (
          <Button
            key={mode.id}
            type="button"
            variant="ghost"
            size="sm"
            disabled={busyMode !== null}
            onClick={() => void runMode(mode.id)}
            title={mode.description}
            className="h-7 gap-1 px-2 text-xs"
          >
            {busyMode === mode.id ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Wand2 className="h-3 w-3" />
            )}
            {mode.label}
          </Button>
        ))}
    </BubbleMenu>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * TipTap returns `<p></p>` for an empty editor. Treat that as the empty
 * string so autosave doesn't fire spuriously when the user opens an empty
 * notes field.
 */
function serialise(html: string): string {
  if (html === "<p></p>") return "";
  return html;
}
